import SEO from '../components/SEO';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiClock, FiUser, FiArrowRight, FiPlay, FiMapPin, FiX } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { VolumeX, Volume2 } from 'lucide-react';
import { fetchSpeeches, getImageSrc, formatMarathiDate } from '../apiService';
import { useLanguage } from '../utils/LanguageContext';

const Speeches = () => {
  const { language } = useLanguage();
  const [speeches, setSpeeches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isMuted, setIsMuted] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadSpeeches();
  }, []);

  const loadSpeeches = async () => {
    try {
      setLoading(true);
      const speechesData = await fetchSpeeches();
      setSpeeches(speechesData);
    } catch (err) {
      console.error('Error loading speeches:', err);
      setError(language === 'MR' ? 'भाषणे लोड करताना त्रुटी' : 'Error loading speeches');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return language === 'MR'
      ? date.toLocaleDateString('mr-IN', { year: 'numeric', month: 'long', day: 'numeric' })
      : date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const truncateText = (text, maxLength = 120) => {
    if (!text) return '';
    if (Array.isArray(text)) {
      const fullText = text.join(' ');
      return fullText.length > maxLength ? fullText.substring(0, maxLength) + '...' : fullText;
    }
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const isNewSpeech = (dateString) => {
    const speechDate = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - speechDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  };

  const getYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const handleCardClick = (speech) => {
    if (speech.videoLink) {
      setSelectedVideo(speech.videoLink);
    } else {
      navigate(`/speech/${speech._id}`);
    }
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
    setIsMuted(true);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const SpeechCard = ({ speech, index }) => (
    <motion.div
      className="bg-white border-2 border-red-100 rounded-xl shadow-md hover:shadow-xl hover:border-red-300 transition-all duration-300 overflow-hidden group relative cursor-pointer"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={{ y: -6, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => handleCardClick(speech)}
    >
      {isNewSpeech(speech.date) && (
        <div className="absolute top-3 right-3 z-10">
          <span className="bg-red-800 text-white px-2 py-1 rounded-full text-xs font-semibold shadow-md">
            {language === 'MR' ? 'नवीन' : 'New'}
          </span>
        </div>
      )}

      {speech.videoLink && (
        <div className="absolute top-3 left-3 z-10">
          <div className="bg-red-800 text-white p-2 rounded-full shadow-md">
            <FiPlay className="w-4 h-4" />
          </div>
        </div>
      )}

      <div className="relative overflow-hidden h-48">
        <img loading="lazy" 
          src={getImageSrc(speech.thumbnail)} 
          alt={speech.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="bg-white text-red-800 px-4 py-2 rounded-full font-semibold flex items-center shadow-md">
            {speech.videoLink ? (
              <>
                <FiPlay className="mr-2 w-4 h-4" />
                {language === 'MR' ? 'पाहा' : 'Watch'}
              </>
            ) : (
              <>
                <FiArrowRight className="mr-2 w-4 h-4" />
                {language === 'MR' ? 'वाचा' : 'Read'}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="p-5">
        <h3 className="text-lg font-bold text-red-800 mb-3 line-clamp-2 group-hover:text-red-600 transition-colors leading-tight">
          {speech.title}
        </h3>

        {speech.content && speech.content[0] && (
          <p className="text-red-600 text-sm line-clamp-3 leading-relaxed mb-4">
            {truncateText(speech.content, 120)}
          </p>
        )}

        {speech.topics && speech.topics.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {speech.topics.slice(0, 2).map((topic, idx) => (
              <span key={idx} className="bg-red-50 text-red-700 text-xs px-2 py-1 rounded-full border border-red-100">
                {topic}
              </span>
            ))}
            {speech.topics.length > 2 && (
              <span className="text-xs text-red-500">+{speech.topics.length - 2}</span>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 text-xs text-red-700 mb-4">
          <div className="flex items-center">
            <FiUser className="mr-1 text-red-600 w-3 h-3" />
            <span className="font-medium">{speech.speaker}</span>
          </div>
          <div className="flex items-center">
            <FiClock className="mr-1 text-red-600 w-3 h-3" />
            <span>{formatDate(speech.date)}</span>
          </div>
          {speech.venue && (
            <div className="flex items-center">
              <FiMapPin className="mr-1 text-red-600 w-3 h-3" />
              <span className="truncate max-w-24">{speech.venue}</span>
            </div>
          )}
        </div>

        <div className="text-center">
          <button className="inline-flex items-center gap-2 bg-red-800 hover:bg-red-900 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300">
            {speech.videoLink ? (
              <>
                <FiPlay className="w-4 h-4" />
                {language === 'MR' ? 'व्हिडिओ पाहा' : 'Watch Video'}
              </>
            ) : (
              <>
                <FiArrowRight className="w-4 h-4" />
                {language === 'MR' ? 'संपूर्ण भाषण वाचा' : 'Read Full Speech'}
              </>
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-white pt-20">
      <SEO title="भाषणे" description="डॉ. राजेंद्र शेंडे यांची आंतरराष्ट्रीय व्यासपीठांवरील भाषणे." canonical="/allspeeches" />
      <div className="max-w-7xl mx-auto px-4 py-8">
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-medium text-red-800 mb-4">
            {language === 'MR' ? 'सर्व मराठी भाषणे' : 'All Marathi Speeches'}
          </h1>
          <div className="w-24 h-1 bg-red-800 mx-auto mb-4" />
          <p className="text-lg text-red-700 max-w-2xl mx-auto">
            {language === 'MR'
              ? 'आमच्या संपूर्ण मराठी भाषण संग्रहातून तुमच्या आवडत्या वक्त्याचे भाषण ऐका'
              : 'Listen to speeches from your favorite speakers from our complete Marathi speeches collection'}
          </p>
        </motion.div>

        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(12)].map((_, index) => (
              <div key={index} className="bg-white border-2 border-red-100 rounded-xl shadow-md overflow-hidden animate-pulse">
                <div className="h-48 bg-gray-300"></div>
                <div className="p-5">
                  <div className="h-4 bg-gray-300 rounded mb-3"></div>
                  <div className="h-4 bg-gray-300 rounded mb-3 w-3/4"></div>
                  <div className="h-3 bg-gray-300 rounded mb-2"></div>
                  <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-12">
            <div className="bg-red-50 border-2 border-red-100 rounded-xl p-8 max-w-md mx-auto">
              <div className="text-6xl text-red-800 mb-4">⚠</div>
              <p className="text-red-800 font-semibold mb-4">{error}</p>
              <button 
                onClick={loadSpeeches}
                className="bg-red-800 hover:bg-red-900 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                {language === 'MR' ? 'पुन्हा प्रयत्न करा' : 'Try Again'}
              </button>
            </div>
          </div>
        )}

        {!loading && !error && speeches.length === 0 && (
          <motion.div 
            className="text-center py-12"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-6xl mb-4">🎤</div>
            <h3 className="text-2xl font-bold text-red-800 mb-2">
              {language === 'MR' ? 'कोणतेही भाषण सापडले नाही' : 'No Speeches Found'}
            </h3>
            <p className="text-red-600">
              {language === 'MR' ? 'सध्या कोणतेही भाषण उपलब्ध नाही' : 'No speeches are currently available'}
            </p>
          </motion.div>
        )}

        {!loading && !error && speeches.length > 0 && (
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            {speeches.map((speech, index) => (
              <SpeechCard key={speech._id} speech={speech} index={index} />
            ))}
          </motion.div>
        )}

        {!loading && !error && speeches.length > 0 && (
          <motion.div 
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <p className="text-red-700 font-medium">
              {language === 'MR'
                ? `एकूण ${speeches.length} भाषणे दाखवत आहे`
                : `Showing ${speeches.length} speeches`}
            </p>
          </motion.div>
        )}
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <motion.div 
          className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="relative w-full max-w-4xl aspect-video bg-black rounded-lg overflow-hidden">
            <button 
              onClick={closeVideoModal}
              className="absolute top-4 right-4 z-50 text-white hover:text-red-400 transition-colors"
            >
              <FiX className="w-8 h-8" />
            </button>

            <div className="absolute top-4 left-4 z-50">
              <button 
                onClick={toggleMute}
                className="text-white hover:text-red-400 transition-colors"
              >
                {isMuted ? (
                  <VolumeX className="w-6 h-6" />
                ) : (
                  <Volume2 className="w-6 h-6" />
                )}
              </button>
            </div>

            <iframe
              src={`https://www.youtube.com/embed/${getYouTubeId(selectedVideo)}?autoplay=1&mute=${isMuted ? 1 : 0}&rel=0&modestbranding=1`}
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="Speech Video"
            />
          </div>
        </motion.div>
      )}

      <style jsx>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default Speeches;